import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usePermissions } from '../hooks/usePermissions';

import { getBackendUrl } from '../utils/getBackendUrl';
import { todayET } from '../utils/timezone';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Icon mapping for common service types
const getServiceIcon = (serviceName) => {
  return FileText; // Default icon for all services
};

// Color mapping for service types
const getServiceColor = (serviceName) => {
  const colors = ['blue', 'green', 'purple', 'orange', 'red', 'pink', 'indigo', 'teal'];
  // Use hash of service name to consistently assign a color
  let hash = 0;
  for (let i = 0; i < serviceName.length; i++) {
    hash = serviceName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const Services = ({ user }) => {
  const navigate = useNavigate();
  const { can, loading: permissionsLoading } = usePermissions();
  const [serviceTypes, setServiceTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [serviceCounts, setServiceCounts] = useState({});
  const [formData, setFormData] = useState({
    client_id: '',
    date: todayET(),
    service_status: 'Pending',
    notes: ''
  });

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  useEffect(() => {
    if (activeTab) {
      fetchServices();
      fetchClients();
      fetchServiceCounts();
    }
  }, [activeTab]);

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/service-types`);
      const types = response.data.service_types || [];
      setServiceTypes(types);
      if (types.length > 0 && !activeTab) {
        setActiveTab(types[0]);
      }
    } catch (error) {
      console.error('Failed to fetch service types:', error);
      toast.error('Failed to load service types');
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?service_type=${encodeURIComponent(activeTab)}`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setClients(response.data.filter(u => u.role === 'client'));
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchServiceCounts = async () => {
    try {
      // Fetch all services to count pending ones per type
      const response = await axios.get(`${API}/services`);
      const counts = {};
      
      serviceTypes.forEach(typeName => {
        const pendingCount = response.data.filter(
          s => s.service_type === typeName && s.service_status === 'Pending'
        ).length;
        counts[typeName] = pendingCount;
      });
      
      setServiceCounts(counts);
    } catch (error) {
      console.error('Failed to fetch service counts:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }

    try {
      await axios.post(`${API}/services`, {
        service_type: activeTab,
        ...formData
      });
      toast.success('Service entry created!');
      setIsDialogOpen(false);
      setFormData({
        client_id: '',
        date: todayET(),
        service_status: 'Pending',
        notes: ''
      });
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service entry');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const Icon = getServiceIcon(activeTab);
  const serviceColor = getServiceColor(activeTab);

  if (loading && serviceTypes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <p className="text-gray-600">Loading service types...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Client Services</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage service requests and jobs</p>
        </div>

        {/* Service Type Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 mb-6">
          {serviceTypes.map((serviceName) => {
            const ServiceIcon = getServiceIcon(serviceName);
            const color = getServiceColor(serviceName);
            const pendingCount = serviceCounts[serviceName] || 0;
            
            const colorClasses = {
              blue: 'bg-blue-600 hover:bg-blue-700',
              green: 'bg-green-600 hover:bg-green-700',
              purple: 'bg-purple-600 hover:bg-purple-700',
              orange: 'bg-orange-600 hover:bg-orange-700',
              red: 'bg-red-600 hover:bg-red-700',
              pink: 'bg-pink-600 hover:bg-pink-700',
              indigo: 'bg-indigo-600 hover:bg-indigo-700',
              teal: 'bg-teal-600 hover:bg-teal-700'
            };
            
            return (
              <Button
                key={serviceName}
                onClick={() => setActiveTab(serviceName)}
                className={`relative min-h-[48px] text-sm ${
                  activeTab === serviceName
                    ? `${colorClasses[color]} text-white`
                    : 'bg-white text-gray-900 border-2 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <ServiceIcon className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                <span className="whitespace-nowrap truncate">{serviceName}</span>
                {pendingCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {pendingCount}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Add Service Button - Admin Only */}
        {(user.role === 'admin' || user.role === 'supervisor') && (permissionsLoading || can('services', 'create')) && (
          <div className="mb-6">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md min-h-[48px]">
                  <Plus className="w-4 h-4 mr-2" />
                  New {activeTab} Entry
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">New {activeTab} Entry</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="font-bold text-base mb-2 block">Client</Label>
                    <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
                      <SelectTrigger className="border-2 border-gray-400 h-12">
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} - {client.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="font-bold text-base mb-2 block">Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="border-2 border-gray-400 h-12"
                    />
                  </div>

                  <div>
                    <Label className="font-bold text-base mb-2 block">Status</Label>
                    <Select value={formData.service_status} onValueChange={(value) => setFormData({...formData, service_status: value})}>
                      <SelectTrigger className="border-2 border-gray-400 h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="font-bold text-base mb-2 block">Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Add any notes or details..."
                      className="border-2 border-gray-400"
                      rows={4}
                    />
                  </div>

                  <Button onClick={handleSubmit} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-lg min-h-[52px]">
                    Create Entry
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Services List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : services.length === 0 ? (
          <div className="bg-white border-2 border-gray-300 rounded-lg p-12 text-center">
            <Icon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg font-semibold">No {activeTab} entries yet</p>
            {(user.role === 'admin' || user.role === 'supervisor') && (
              <p className="text-gray-500 mt-2">Click "New {activeTab} Entry" to get started</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => {
              const isPending = service.service_status === 'Pending';
              return (
                <div
                  key={service.id}
                  onClick={() => navigate(`/services/${service.id}`)}
                  className={`relative bg-white rounded-lg p-4 cursor-pointer hover:shadow-lg transition-all ${
                    isPending 
                      ? 'border-2 border-red-500 shadow-md' 
                      : 'border-2 border-gray-300 hover:border-blue-600'
                  }`}
                >
                  {isPending && (
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                      Needs Attention
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{service.client_name}</h3>
                      <p className="text-sm text-gray-600">{new Date(service.date).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(service.service_status)}`}>
                      {service.service_status}
                    </span>
                  </div>
                  {service.notes && (
                    <p className="text-sm text-gray-700 line-clamp-2">{service.notes}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;
